// Scoreholio Design Guide 2027 tokens. Light and dark are separate sets, not an inversion.
import { useSelector } from "react-redux";
import { uiOrange, uiBlue, uiGreen, uiPurple, uiPurpleLight } from "./Global";

// Organizer plan colors by subLevelNum (brand, Global.jsx). grad: chips, badges, lock tiles.
// text / tint: an Unlock button's label and wash; dark sets lift the text so it reads on #1F2A34.
// deep: tier text on a white pill (both modes).
const tierSet = (dark) => [
  { color: uiOrange, grad: `linear-gradient(135deg, #E89A55 0%, ${uiOrange} 100%)`, deep: "#9C5A1F", text: dark ? "#EDAE72" : "#B5651D", tint: dark ? "rgba(208,122,45,0.24)" : "rgba(208,122,45,0.16)" },
  { color: uiBlue, grad: `linear-gradient(135deg, #5B98C9 0%, ${uiBlue} 100%)`, deep: "#24577E", text: dark ? "#8CC2EA" : "#2C6593", tint: dark ? "rgba(53,118,164,0.30)" : "rgba(53,118,164,0.16)" },
  { color: uiGreen, grad: `linear-gradient(135deg, #66B34B 0%, ${uiGreen} 100%)`, deep: "#2F6C1D", text: dark ? "#8BDA6F" : "#357A20", tint: dark ? "rgba(67,146,42,0.26)" : "rgba(67,146,42,0.16)" },
  { color: dark ? uiPurpleLight : uiPurple, grad: `linear-gradient(135deg, ${uiPurpleLight} 0%, ${uiPurple} 100%)`, deep: "#3A2563", text: dark ? "#C2A6F5" : "#5E3AA8", tint: dark ? "rgba(137,90,219,0.26)" : "rgba(75,49,121,0.14)" },
];

const shared = {
  font: '"Montserrat", sans-serif',
  brandRed: "#CC0C0D",
  sprGold: "linear-gradient(135deg, #F6DC72 0%, #D4A805 100%)",
  sprGoldText: "#3B2C00",
  // RAT home logged-out header band (SCO-2779): neutral, no plan.
  guestBand: "linear-gradient(135deg, #5B6670 0%, #2F3A44 100%)",
  ease: "cubic-bezier(.2,.8,.2,1)",
  radius: { card: 20, tile: 14, button: 14, chip: 9 },
  gutter: 16,
  cardPadding: 20,
  // Extra room under every 2027 sheet's last content, plus Android's gesture strip (iOS already lifts sheets above it).
  sheetEndRoom: "calc(24px + var(--sh-chrome-bottom, 0px) - var(--sh-sheet-bottom, 0px))",
  // Podium metals: step face, place chip, halo. Same in light and dark.
  metal: {
    1: { bg: "linear-gradient(145deg,#FFF1B3 0%,#F2CD3C 45%,#C99A00 100%)", fg: "#3B2C00", label: "#5E4700", border: "1px solid rgba(160,120,0,0.35)", dot: "#D4A805", halo: "242,194,48" },
    2: { bg: "linear-gradient(145deg,#FFFFFF 0%,#DDE3EA 45%,#A9B4C0 100%)", fg: "#28303A", label: "#46505C", border: "1px solid rgba(90,100,115,0.3)", dot: "#9AA6B2", halo: "158,172,188" },
    3: { bg: "linear-gradient(145deg,#FBDDC0 0%,#D99A5E 48%,#9C6230 100%)", fg: "#2E1B09", label: "#4A2E14", border: "1px solid rgba(120,70,30,0.3)", dot: "#B5733A", halo: "217,154,94" },
  },
};

export const theme2027 = {
  light: {
    ...shared,
    bg: "#C0D4DE",
    card: "#FFFFFF",
    subtle: "#F1F4F6",
    text: "#2D2B2D",
    muted: "#5F666E",
    faint: "#8E959C",
    line: "rgba(20,40,60,0.10)",
    red: "#CC0C0D",
    redTint: "#F8E0E2",
    redGlow: "0 1px 2px rgba(204,12,13,0.14), 0 5px 12px rgba(204,12,13,0.16)",
    green: "#2E7D18",
    greenTint: "#E1F2DA",
    blue: "#2F6FD0",
    chartBlue: "#2F6FD0",
    blueTint: "#DCE8F8",
    amber: "#D9901E",
    amberTint: "#F8EBD3",
    amberText: "#9A5F06",
    mid: "#A7AFB8",
    menu: "#FFFFFF",
    skeletonShine: "rgba(255,255,255,0.45)",
    menuShadow: "0 2px 6px rgba(20,34,48,0.10), 0 12px 28px rgba(20,34,48,0.18)",
    accentBlue: "#3576a4",
    rankBlue: "#2F6C99",
    rankBlueTint: "#DCEDF5",
    // Seasons accent (the home tile's uiPurpleLight).
    purple: "#895ADB",
    purpleTint: "#ECD2F5",
    track: "#E7ECF0",
    // Translucent, so it also reads on a tinted group.
    stripe: "rgba(20,40,60,0.04)",
    sprOff: "linear-gradient(135deg, #EEF1F4 0%, #CBD2D9 100%)",
    sprOffText: "#5F666E",
    segOn: "#FFFFFF",
    segShadow: "0 1px 2px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.04)",
    navTrack: "rgba(231,236,240,0.82)",
    navShadow: "0 1px 2px rgba(20,34,48,0.06), 0 4px 10px rgba(20,34,48,0.08)",
    series: {
      in: "#CC0C0D",
      inTint: "#F8E0E2",
      on: "#2F6FD0",
      off: "#D9901E",
      won: "#2E7D18",
      tied: "#D9901E",
      lost: "#CC0C0D",
    },
    lockBg: "rgba(255,255,255,0.35)",
    editedTint: "#FBF0D9",
    cardShadow: "0 1px 3px rgba(20,34,48,0.10), 0 4px 12px rgba(20,34,48,0.10)",
    chip: "rgba(255,255,255,0.94)",
    tier: tierSet(false),
    // RAT home Create New card (SCO-2778): the red wash, its estimator strip, hairline and chevron disc.
    createWash: "linear-gradient(280deg, #F6D3D6 0%, #F8E0E2 45%, #FDF2F3 100%)",
    // Phil 2026-10-08: the estimator strip is blue so it reads as its own button.
    createStrip: "#DCE8F8",
    createLine: "rgba(47,111,208,0.16)",
    createChev: "rgba(204,12,13,0.12)",
    createInk: "#2D2B2D",
    createInkMuted: "#5F666E",
    createBlue: "#2F6FD0",
    createGlow: "0 1px 2px rgba(204,12,13,0.14), 0 5px 12px rgba(204,12,13,0.16)",
    createWm: 0.1,
    createPlusShadow: "0 3px 8px rgba(204,12,13,0.32)",
    // Notifications 2027 (SCO-2961): one ink per kind of notification and per button (tile, chip, button).
    kinds: { red: "#CC0C0D", amber: "#B26F0A", blue: "#2F6FD0", green: "#2E7D18", brown: "#8B5E34", orange: "#D2620A", purple: "#7C5CFF", gold: "#9A7300", grey: "#5F666E" },
    unread: "rgba(47,111,208,0.07)",
  },
  dark: {
    ...shared,
    bg: "#131A1F",
    card: "#1F2A34",
    subtle: "#18222B",
    text: "#EBF1F4",
    muted: "#A3AFBA",
    faint: "#7D8A96",
    line: "rgba(255,255,255,0.08)",
    red: "#FF6B6B",
    redTint: "rgba(204,12,13,0.24)",
    redGlow: "0 1px 2px rgba(0,0,0,0.4), 0 5px 14px rgba(255,64,64,0.2)",
    green: "#74D957",
    greenTint: "rgba(67,146,42,0.24)",
    blue: "#5B9BE8",
    chartBlue: "#4A8BDB",
    blueTint: "rgba(74,139,219,0.22)",
    amber: "#C4852A",
    amberTint: "rgba(196,133,42,0.22)",
    amberText: "#E9A94B",
    mid: "#5E6B77",
    menu: "#27343F",
    skeletonShine: "rgba(255,255,255,0.07)",
    menuShadow: "0 12px 28px rgba(0,0,0,0.45)",
    accentBlue: "#7DB8E3",
    rankBlue: "#7DB8E3",
    rankBlueTint: "rgba(53,118,164,0.28)",
    purple: "#B394F0",
    purpleTint: "rgba(137,90,219,0.24)",
    track: "#2A3743",
    stripe: "rgba(255,255,255,0.035)",
    sprOff: "linear-gradient(135deg, #3B4956 0%, #2A3743 100%)",
    sprOffText: "#A3AFBA",
    // Lighter than `track` plus a lift, or the selected pill vanishes into it (was #2E3C49 / none).
    segOn: "#475867",
    segShadow: "0 1px 3px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
    navTrack: "rgba(27,37,46,0.86)",
    navShadow: "0 4px 12px rgba(0,0,0,0.35)",
    series: {
      in: "#E24C4C",
      inTint: "rgba(204,12,13,0.24)",
      on: "#4A8BDB",
      off: "#C4852A",
      won: "#4CB33E",
      tied: "#C4852A",
      lost: "#E24C4C",
    },
    lockBg: "rgba(19,26,31,0.35)",
    editedTint: "rgba(217,144,30,0.18)",
    cardShadow: "none",
    chip: "rgba(31,42,52,0.94)",
    tier: tierSet(true),
    // Phil 2026-10-08, round E1 "rose slate" (mock #rat/home/f3-6): the light card's colors carried into the dark page.
    createWash: "linear-gradient(280deg, #3A2B33 0%, #332930 45%, #2A2630 100%)",
    createStrip: "#22324A",
    createLine: "rgba(125,176,238,0.16)",
    createChev: "rgba(255,107,107,0.16)",
    createInk: "#EBF1F4",
    createInkMuted: "#B8ADB3",
    createBlue: "#7DB0EE",
    createGlow: "none",
    createWm: 0.06,
    createPlusShadow: "0 2px 6px rgba(0,0,0,0.35)",
    kinds: { red: "#FF6B6B", amber: "#E9A94B", blue: "#5B9BE8", green: "#74D957", brown: "#D2A27A", orange: "#F39A55", purple: "#A08BFF", gold: "#E6C04A", grey: "#A3AFBA" },
    unread: "rgba(91,155,232,0.10)",
  },
};

export function useTheme2027() {
  const darkMode = useSelector((state) => state.global.darkMode);
  return darkMode ? theme2027.dark : theme2027.light;
}
